import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { config } from '../config/environment';

const OnboardingRouter: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      console.log('🔄 OnboardingRouter - Checking status...');
      console.log('🔄 OnboardingRouter - Current URL params:', searchParams.toString());
      
      // Check for flow ID in URL first, then localStorage
      let flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
      const existingFlowId = localStorage.getItem('onboardingFlowId');
      
      console.log('🔄 OnboardingRouter - URL flow ID:', flowId);
      console.log('🔄 OnboardingRouter - Existing localStorage flow ID:', existingFlowId);
      
      // Priority: URL flow ID > localStorage flow ID
      if (flowId) {
        console.log('🔄 OnboardingRouter - Using flow ID from URL:', flowId);
        // Store in localStorage for future navigation
        localStorage.setItem('onboardingFlowId', flowId);
        // Check the status of the flow from URL
        const flowData = await checkFlowStatus(flowId);
        redirectBasedOnFlowData(flowData, flowId);
      } else if (existingFlowId) {
        console.log('🔄 OnboardingRouter - Using flow ID from localStorage:', existingFlowId);
        // Check the status of existing flow
        const flowData = await checkFlowStatus(existingFlowId);
        redirectBasedOnFlowData(flowData, existingFlowId);
      } else {
        // No existing flow - redirect to getting started with any URL params intact
        const urlParams = new URLSearchParams(searchParams);
        const paramString = urlParams.toString();
        const targetUrl = `/getting-started${paramString ? `?${paramString}` : ''}`;
        console.log('🔄 OnboardingRouter - No flow ID found, redirecting to:', targetUrl);
        navigate(targetUrl);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      navigate('/getting-started');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const checkFlowStatus = async (flowId: string) => {
    try {
      const response = await fetch(`${config.api.onboardingFlows}/${flowId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const flow = await response.json();
        console.log('🔄 OnboardingRouter - Flow data:', flow);
        return flow;
      } else {
        console.error('🔄 OnboardingRouter - Flow not found:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error fetching flow status:', error);
      return null;
    }
  };

  const redirectBasedOnFlowData = (flowData: any, flowId: string) => {
    if (!flowData) {
      console.log('🔄 OnboardingRouter - Invalid flow, redirecting to getting started');
      // Clear invalid flow ID from localStorage
      localStorage.removeItem('onboardingFlowId');
      navigate('/getting-started');
      return;
    }

    const { status } = flowData;
    console.log('🔄 OnboardingRouter - Flow status:', status);

    // Always add flow ID to URL for persistence
    const flowParam = `?flow=${flowId}`;

    // Use the exact enum values from OnboardingFlowStatus (action-oriented)
    switch (status) {
      case 'INITIATED':
        // INITIATED means either:
        // 1. Just created (emailVerifiedVia = null) → redirect to verify-email
        // 2. Email verified but not paid yet (emailVerifiedVia != null) → shouldn't happen, should be EMAIL_VERIFIED status
        // For safety, redirect to verify-email if emailVerifiedVia is null, otherwise to getting-started
        if (!flowData.emailVerifiedVia) {
          navigate(`/verify-email${flowParam}`);
        } else {
          // Email was verified but status not updated properly - redirect to plans
          navigate(`/select-plans${flowParam}`);
        }
        break;
      case 'EMAIL_VERIFIED':
        // Email verified, redirect to plan selection
        navigate(`/select-plans${flowParam}`);
        break;
      case 'PAID':
        // Payment completed, proceed to create organization
        navigate(`/create-organization${flowParam}`);
        break;
      case 'CREATING_ORG':
        navigate(`/create-organization${flowParam}`);
        break;
      case 'CONFIGURING_STRUCTURE':
        // Organization created, now configure structure
        navigate(`/configure-structure${flowParam}`);
        break;
      case 'CUSTOMIZING_FORMS':
        // Structure configured, now customize forms
        navigate(`/customize-forms${flowParam}`);
        break;
      case 'COMPLETE':
        navigate(`/complete${flowParam}`);
        break;
      case 'FAILED':
        // Redirect to getting started for failed flows
        navigate(`/getting-started${flowParam}`);
        break;
      default:
        console.warn('🔄 OnboardingRouter - Unknown status:', status);
        navigate(`/getting-started${flowParam}`);
    }
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner color="primary" size="lg" />
          <p className="mt-3 text-muted">Setting up your onboarding...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default OnboardingRouter;